(() => {
  let selectedProductHandles = new Set();
  let cartQuantityByVariant = new Map();
  let cartRequestController = null;
  let cartQuantitiesInitialized = false;

  const syncAiButtons = (root = document) => {
    root.querySelectorAll?.('.jsd-popular-card').forEach((card) => {
      const button = card.querySelector('.jsd-popular-card__ai');
      const handle = button?.dataset.productHandle;
      const selected = Boolean(handle && selectedProductHandles.has(handle));
      const wasSelected = card.classList.contains('is-ai-added');
      card.classList.toggle('is-ai-added', selected);
      card.classList.toggle('is-ai-removing', wasSelected && !selected);
      if (wasSelected && !selected) {
        window.setTimeout(() => card.classList.remove('is-ai-removing'), 500);
      }
      button?.setAttribute('aria-pressed', String(selected));
      button?.querySelector('.jsd-popular-card__ai-added')?.setAttribute('aria-hidden', String(!selected));
    });
  };

  const syncCartButtons = (root = document, { animateChanges = false } = {}) => {
    root.querySelectorAll?.('.jsd-popular-card__cart[data-jsd-cart-variant]').forEach((button) => {
      const label = button.querySelector('[data-jsd-cart-label]');
      if (!label || button.disabled) return;
      const quantity = cartQuantityByVariant.get(String(button.dataset.jsdCartVariant)) || 0;
      const previousQuantity = Number(button.dataset.jsdCartQuantity);
      const quantityChanged = button.dataset.jsdCartQuantity !== undefined && previousQuantity !== quantity;
      button.dataset.jsdCartQuantity = String(quantity);
      button.classList.toggle('has-cart-quantity', quantity > 0);
      label.textContent = quantity > 0 ? `Add To Cart (${quantity})` : 'Add To Cart';
      button.setAttribute('aria-label', quantity > 0
        ? `Add To Cart. ${quantity} currently in cart`
        : 'Add To Cart');
      if (animateChanges && quantityChanged) {
        button.classList.remove('is-cart-updated');
        void button.offsetWidth;
        button.classList.add('is-cart-updated');
        window.setTimeout(() => button.classList.remove('is-cart-updated'), 450);
      }
    });
  };

  const loadCartQuantities = async () => {
    cartRequestController?.abort();
    const controller = new AbortController();
    cartRequestController = controller;
    try {
      const cartUrl = `${window.routes?.cart_url || '/cart'}.js`;
      const response = await fetch(cartUrl, {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
        signal: controller.signal,
      });
      if (!response.ok) throw new Error(`Cart request failed with status ${response.status}`);
      const cart = await response.json();
      cartQuantityByVariant = new Map((cart.items || []).map((item) => [String(item.variant_id), Number(item.quantity) || 0]));
      syncCartButtons(document, { animateChanges: cartQuantitiesInitialized });
      cartQuantitiesInitialized = true;
    } catch (error) {
      if (error.name !== 'AbortError') console.warn('JSD product card cart counts could not be refreshed.', error);
    } finally {
      if (cartRequestController === controller) cartRequestController = null;
    }
  };

  document.addEventListener('jsd-chatbot:reference-products-change', (event) => {
    selectedProductHandles = new Set(Array.isArray(event.detail?.handles) ? event.detail.handles : []);
    syncAiButtons();
  });

  const observeDynamicCards = () => {
    syncAiButtons();
    syncCartButtons();
    const observerRoot = document.documentElement;
    if (!observerRoot || observerRoot.nodeType !== 1 || typeof MutationObserver === 'undefined') return;
    new MutationObserver((records) => {
      records.forEach((record) => record.addedNodes.forEach((node) => {
        if (!(node instanceof Element)) return;
        if (node.matches('.jsd-popular-card')) {
          syncAiButtons(node.parentElement || node);
          syncCartButtons(node.parentElement || node);
        } else if (node.querySelector?.('.jsd-popular-card')) {
          syncAiButtons(node);
          syncCartButtons(node);
        }
      }));
    }).observe(observerRoot, { childList: true, subtree: true });
  };

  const initializeCards = () => {
    observeDynamicCards();
    loadCartQuantities();
    if (typeof subscribe === 'function' && window.PUB_SUB_EVENTS?.cartUpdate) {
      subscribe(window.PUB_SUB_EVENTS.cartUpdate, loadCartQuantities);
    } else if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, loadCartQuantities);
    }
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initializeCards, { once: true });
  else initializeCards();

  if (customElements.get('jsd-popular-products')) return;

  class JsdPopularProducts extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.grid = this.querySelector('[data-popular-grid]');
      this.heading = this.querySelector('[data-popular-heading]');
      this.status = this.querySelector('[data-popular-status]');
      this.defaultHandle = this.dataset.defaultHandle || 'best-sellers';
      this.currentHandle = this.defaultHandle;
      this.requestController = null;

      document.addEventListener('jsd:vehicle-change', (event) => this.applyVehicle(event.detail));
      syncAiButtons(this);
      this.applyVehicle(window.JsdVehicle?.get?.() || null, { initial: true });
    }

    async applyVehicle(vehicle, { initial = false } = {}) {
      const handle = vehicle?.collection || this.defaultHandle;
      const nextHeading = vehicle ? this.dataset.vehicleHeading : this.dataset.defaultHeading;
      if (this.heading) this.heading.textContent = nextHeading;

      if (initial && !vehicle) {
        this.syncFitmentBadges(null, this);
        return;
      }
      if (handle === this.currentHandle && initial) {
        this.syncFitmentBadges(vehicle, this);
        return;
      }
      await this.loadCollection(handle, vehicle);
    }

    async loadCollection(handle, vehicle) {
      this.requestController?.abort();
      const controller = new AbortController();
      this.requestController = controller;
      this.setLoading(true);

      try {
        const root = window.Shopify?.routes?.root || '/';
        const url = `${root.replace(/\/$/, '')}/collections/${encodeURIComponent(handle)}?view=popular-products&sort_by=best-selling`;
        const response = await fetch(url, {
          headers: { 'X-Requested-With': 'XMLHttpRequest' },
          signal: controller.signal,
        });
        if (!response.ok) throw new Error(`Product request failed with status ${response.status}`);

        const html = await response.text();
        this.grid.innerHTML = html;
        this.currentHandle = handle;
        this.syncFitmentBadges(vehicle, this.grid);
        syncAiButtons(this.grid);
        if (typeof initializeScrollAnimationTrigger === 'function') {
          initializeScrollAnimationTrigger(this.grid);
        }
        if (this.status) this.status.textContent = `${this.grid.children.length} products loaded`;
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.warn('JSD popular products could not be refreshed.', error);
          if (this.status) this.status.textContent = 'Products could not be refreshed';
        }
      } finally {
        if (this.requestController === controller) this.setLoading(false);
      }
    }

    setLoading(loading) {
      if (loading) this.setAttribute('aria-busy', 'true');
      else this.removeAttribute('aria-busy');
      if (loading && this.status) this.status.textContent = this.dataset.loadingLabel || 'Loading products';
    }

    syncFitmentBadges(vehicle, root) {
      window.JsdVehicle?.updateFitmentBadges?.(vehicle, root);
      root.querySelectorAll?.('.jsd-fitment-badge.is-fit [data-jsd-fitment-label]')
        .forEach((label) => { label.textContent = 'Compatible'; });
    }
  }

  customElements.define('jsd-popular-products', JsdPopularProducts);
})();
